import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PhoneIcon, MailIcon, InstagramIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
const initial = {
  name: '',
  email: '',
  phone: '',
  type: '',
  date: '',
  message: ''
};
export function Contact() {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k: string, v: string) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      toast.success(t('contact.toast'));
      setForm(initial);
      setLoading(false);
    }, 600);
  };
  const inputCls = 'w-full bg-ink border border-gold/20 px-4 py-3 text-cream placeholder-cream/30 focus:border-gold focus:outline-none transition-colors rounded-sm';
  return <section id="contacto" className="py-24 md:py-32 bg-ink2 relative" data-id="element-33">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,164,55,0.08),_transparent_60%)] pointer-events-none" data-id="element-34" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-35">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} data-id="element-36">
          <p className="text-gold text-xs tracking-[0.3em] mb-4" data-id="element-37">
            {t('contact.eyebrow')}
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-cream leading-tight mb-6" data-id="element-38">
            {t('contact.title.1')}{' '}
            <span className="italic gold-text" data-id="element-39">{t('contact.title.2')}</span>
          </h2>
          <p className="text-cream/70 text-lg mb-10 leading-relaxed" data-id="element-40">
            {t('contact.intro')}
          </p>

          <ul className="space-y-5" data-id="element-41">
            <li className="flex items-center gap-4" data-id="element-42">
              <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold" data-id="element-43">
                <PhoneIcon size={16} data-id="element-44" />
              </span>
              <a href="tel:+14077707625" className="text-cream hover:text-gold transition-colors" data-id="element-45">
                (407) 770-7625
              </a>
            </li>
            <li className="flex items-center gap-4" data-id="element-46">
              <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold" data-id="element-47">
                <MailIcon size={16} data-id="element-48" />
              </span>
              <a href="mailto:jadelatinmusic@gmail.com" className="text-cream hover:text-gold transition-colors" data-id="element-49">
                jadelatinmusic@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-4" data-id="element-50">
              <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold" data-id="element-51">
                <InstagramIcon size={16} data-id="element-52" />
              </span>
              <a href="https://instagram.com/jademusic" className="text-cream hover:text-gold transition-colors" data-id="element-53">
                @jademusic
              </a>
            </li>
            <li className="flex items-center gap-4" data-id="element-54">
              <span className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold" data-id="element-55">
                <MapPinIcon size={16} data-id="element-56" />
              </span>
              <span className="text-cream" data-id="element-57">Orlando, Florida</span>
            </li>
          </ul>

          <p className="text-cream/40 text-sm mt-10 italic" data-id="element-58">
            {t('contact.response')}
          </p>
        </motion.div>

        <motion.form onSubmit={submit} initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="bg-ink border border-gold/20 p-6 md:p-10 rounded-sm space-y-4" data-id="element-59">
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-60">
            <div data-id="element-61">
              <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-62">
                {t('contact.form.name')}
              </label>
              <input required value={form.name} onChange={e => update('name', e.target.value)} className={inputCls} placeholder={t('contact.form.name.ph')} data-id="element-63" />
            </div>
            <div data-id="element-64">
              <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-65">
                {t('contact.form.email')}
              </label>
              <input required type="email" value={form.email} onChange={e => update('email', e.target.value)} className={inputCls} placeholder="tu@email.com" data-id="element-66" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4" data-id="element-67">
            <div data-id="element-68">
              <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-69">
                {t('contact.form.phone')}
              </label>
              <input required value={form.phone} onChange={e => update('phone', e.target.value)} className={inputCls} placeholder="(407) 000-0000" data-id="element-70" />
            </div>
            <div data-id="element-71">
              <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-72">
                {t('contact.form.date')}
              </label>
              <input type="date" value={form.date} onChange={e => update('date', e.target.value)} className={inputCls} data-id="element-73" />
            </div>
          </div>

          <div data-id="element-74">
            <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-75">
              {t('contact.form.type')}
            </label>
            <select required value={form.type} onChange={e => update('type', e.target.value)} className={inputCls} data-id="element-76">
              <option value="" data-id="element-77">{t('contact.form.type.placeholder')}</option>
              <option value="boda" data-id="element-78">{t('contact.form.type.boda')}</option>
              <option value="corporativo" data-id="element-79">{t('contact.form.type.corp')}</option>
              <option value="cumpleanos" data-id="element-80">{t('contact.form.type.bday')}</option>
              <option value="restaurante" data-id="element-81">{t('contact.form.type.rest')}</option>
              <option value="otro" data-id="element-82">{t('contact.form.type.other')}</option>
            </select>
          </div>

          <div data-id="element-83">
            <label className="text-xs tracking-wider text-cream/60 mb-2 block" data-id="element-84">
              {t('contact.form.message')}
            </label>
            <textarea rows={4} value={form.message} onChange={e => update('message', e.target.value)} className={inputCls} placeholder={t('contact.form.message.ph')} data-id="element-85" />
          </div>

          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gold text-ink font-medium rounded-full hover:bg-gold-light transition-colors disabled:opacity-60" data-id="element-86">
            {loading ? t('contact.form.sending') : t('contact.form.submit')}
            <SendIcon size={16} data-id="element-87" />
          </button>
        </motion.form>
      </div>
    </section>;
}